import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Package, 
  ShoppingCart, 
  ClipboardList, 
  LogOut,
  Menu,
  X,
  Home,
  User,
  Bell,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import axios from 'axios';
import CustomerInstallPrompt from './CustomerInstallPrompt';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomerLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Check if customer is logged in
    const token = localStorage.getItem('nas_customer_token');
    const customerData = localStorage.getItem('nas_customer');
    
    if (!token || !customerData) {
      navigate('/nas-portal/login');
      return;
    }
    
    setCustomer(JSON.parse(customerData));
    
    // Get cart count
    const cart = JSON.parse(localStorage.getItem('nas_customer_cart') || '[]');
    setCartCount(cart.length);
  }, [navigate, location]);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      const cart = JSON.parse(localStorage.getItem('nas_customer_cart') || '[]');
      setCartCount(cart.length);
    };
    
    window.addEventListener('nas_cart_updated', handleCartUpdate);
    return () => window.removeEventListener('nas_cart_updated', handleCartUpdate);
  }, []);

  // Fetch notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      const token = localStorage.getItem('nas_customer_token');
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/api/nas-customer/notifications/unread-count?token=${token}`);
        setNotificationCount(response.data.unread_count || 0);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
      }
    };
    
    fetchNotificationCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('nas_customer_token');
    localStorage.removeItem('nas_customer');
    localStorage.removeItem('nas_customer_cart');
    toast.success('Logged out successfully');
    navigate('/nas-portal/login');
  };

  const NAV_ITEMS = [
    { path: '/nas-portal/catalog', label: 'Products', icon: Package },
    { path: '/nas-portal/cart', label: 'Cart', icon: ShoppingCart, badge: cartCount },
    { path: '/nas-portal/orders', label: 'My Orders', icon: ClipboardList },
    { path: '/nas-portal/invoices', label: 'Invoices', icon: FileText },
    { path: '/nas-portal/notifications', label: 'Notifications', icon: Bell, badge: notificationCount },
    { path: '/nas-portal/profile', label: 'Profile', icon: User },
  ];

  if (!customer) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 z-40 px-4">
        <div className="h-full max-w-6xl mx-auto flex items-center justify-between">
          {/* Mobile Menu Button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </Button>
          
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
              <Package size={18} className="text-white" />
            </div>
            <span className="font-bold text-slate-800 hidden sm:block">NAS Portal</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  variant={isActive ? "secondary" : "ghost"}
                  className={cn(
                    "relative",
                    isActive && "bg-green-50 text-green-700"
                  )}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon size={18} className="mr-2" />
                  {item.label}
                  {item.badge > 0 && (
                    <Badge className="ml-2 bg-green-600">{item.badge}</Badge>
                  )}
                </Button>
              );
            })}
          </nav>
          
          {/* User & Logout */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600 hidden sm:block">
              {customer.name}
            </span>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              data-testid="customer-logout-btn"
            >
              <LogOut size={18} className="text-slate-500" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Mobile Sidebar */}
      <aside className={cn(
        "lg:hidden fixed top-0 left-0 h-full w-64 bg-white z-50 transform transition-transform duration-200",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="h-14 flex items-center justify-between px-4 border-b">
          <div className="flex items-center gap-2">
            <Package size={20} className="text-green-600" />
            <span className="font-bold">NAS Portal</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
            <X size={18} />
          </Button>
        </div>
        
        <div className="p-4">
          <p className="text-sm text-slate-500 mb-2">Welcome,</p>
          <p className="font-semibold text-slate-800">{customer.name}</p>
        </div>
        
        <nav className="p-2 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.path}
                variant={isActive ? "secondary" : "ghost"}
                className={cn(
                  "w-full justify-start",
                  isActive && "bg-green-50 text-green-700"
                )}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
              >
                <Icon size={18} className="mr-2" />
                {item.label}
                {item.badge > 0 && (
                  <Badge className="ml-auto bg-green-600">{item.badge}</Badge>
                )}
              </Button>
            );
          })}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={handleLogout}
          >
            <LogOut size={18} className="mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="pt-14 min-h-screen">
        <div className="max-w-6xl mx-auto">
          {/* Install App Prompt */}
          <CustomerInstallPrompt />
          
          <Outlet context={{ customer, cartCount }} />
        </div>
      </main>
      
      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-slate-200 z-30">
        <div className="h-full flex items-center justify-around">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                className={cn(
                  "flex flex-col items-center justify-center px-4 py-2 relative",
                  isActive ? "text-green-600" : "text-slate-500"
                )}
                onClick={() => navigate(item.path)}
              >
                <div className="relative">
                  <Icon size={22} />
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-2 w-5 h-5 bg-green-600 text-white text-xs rounded-full flex items-center justify-center">
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className="text-xs mt-1">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default CustomerLayout;
